import React, { useState, useEffect, useCallback, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { BsArrowLeft } from "react-icons/bs";
import { Tabs, Tab } from "@mui/material";
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  ResponsiveContainer,
} from "recharts";
import { HttpService } from "../../service/HttpService";
import FullScreenLoader from "../../components/Loader/FullScreenLoader";
import { useAuth } from "../../context/AuthProvider";
import "./AverageRoomLogReport.css";
import dayjs from "dayjs";

const COLOR_CURRENT = "#00A272"; // green  → current
const COLOR_PREVIOUS = "#BD3C86"; // blue   → previous
const COLOR_TARGET = "#2979FF"; // green  → target

const STATUS_COLORS = {
  RED: "#FF5252",
  YELLOW: "#FFB300",
  GREEN: "#00A272",
};

const CHART_CONFIGS = [
  // 1. Water Temp
  {
    title: "Water Temp – Epi 01 (°F)",
    lines: [
      { key: "E1_water_temp_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E1_latest_water_temp",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E1_latest_water_temp_color",
      },
      {
        key: "E1_avg_water_temp",
        label: "Cultivar Average",
        color: COLOR_PREVIOUS,
      },
    ],
  },
  {
    title: "Water Temp – Epi 02 (°F)",
    lines: [
      { key: "E2_water_temp_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E2_latest_water_temp",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E2_latest_water_temp_color",
      },
      {
        key: "E2_avg_water_temp",
        label: "Cultivar Average",
        color: COLOR_PREVIOUS,
      },
    ],
  },
  {
    title: "Water Temp – Epi 03 (°F)",
    lines: [
      { key: "E3_water_temp_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E3_latest_water_temp",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E3_latest_water_temp_color",
      },
      {
        key: "E3_avg_water_temp",
        label: "Cultivar Average",
        color: COLOR_PREVIOUS,
      },
    ],
  },
  // 2. PH
  {
    title: "pH – Epi 01",
    lines: [
      { key: "E1_ph_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E1_latest_ph",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E1_latest_ph_color",
      },
      { key: "E1_avg_ph", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
  {
    title: "pH – Epi 02",
    lines: [
      { key: "E2_ph_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E2_latest_ph",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E2_latest_ph_color",
      },
      { key: "E2_avg_ph", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
  {
    title: "pH – Epi 03",
    lines: [
      { key: "E3_ph_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E3_latest_ph",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E3_latest_ph_color",
      },
      { key: "E3_avg_ph", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
  // 3. TDS
  {
    title: "TDS – Epi 01 (ppm)",
    lines: [
      { key: "E1_tds_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E1_latest_tds",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E1_latest_tds_color",
      },
      { key: "E1_avg_tds", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
  {
    title: "TDS – Epi 02 (ppm)",
    lines: [
      { key: "E2_tds_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E2_latest_tds",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E2_latest_tds_color",
      },
      { key: "E2_avg_tds", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
  {
    title: "TDS – Epi 03 (ppm)",
    lines: [
      { key: "E3_tds_target", label: "Target", color: COLOR_TARGET },
      {
        key: "E3_latest_tds",
        label: "Current Parameter",
        color: COLOR_CURRENT,
        colorKey: "E3_latest_tds_color",
      },
      { key: "E3_avg_tds", label: "Cultivar Average", color: COLOR_PREVIOUS },
    ],
  },
];

const CATEGORY_CONFIG = [
  {
    name: "Water Temperature",
    charts: [
      "Water Temp – Epi 01 (°F)",
      "Water Temp – Epi 02 (°F)",
      "Water Temp – Epi 03 (°F)",
    ],
    defaultSelected: [
      "Water Temp – Epi 01 (°F)",
      "Water Temp – Epi 02 (°F)",
      "Water Temp – Epi 03 (°F)",
    ],
  },
  {
    name: "PH",
    charts: ["pH – Epi 01", "pH – Epi 02", "pH – Epi 03"],
    defaultSelected: ["pH – Epi 01", "pH – Epi 02", "pH – Epi 03"],
  },
  {
    name: "TDS",
    charts: ["TDS – Epi 01 (ppm)", "TDS – Epi 02 (ppm)", "TDS – Epi 03 (ppm)"],
    defaultSelected: [
      "TDS – Epi 01 (ppm)",
      "TDS – Epi 02 (ppm)",
      "TDS – Epi 03 (ppm)",
    ],
  },
];

const tabsSx = {
  backgroundColor: "#fffffc",
  "& .MuiTabs-indicator": { backgroundColor: "#00A272", height: "3px" },
  "& .MuiTab-root": {
    position: "relative",
    color: "#A1A2A1",
    fontWeight: 400,
    fontSize: "16px",
    textTransform: "none",
    padding: "12px 20px",
    marginRight: "8px",
    "&:not(.Mui-selected)::after": {
      content: '""',
      position: "absolute",
      left: 0,
      right: 0,
      bottom: 0,
      height: "3px",
      backgroundColor: "#C4C4C4",
    },
    "&.Mui-selected": {
      color: "#00A272 !important",
      fontWeight: 600,
      backgroundColor: "#E6F6F1",
      borderRadius: "4px 4px 0 0",
    },
  },
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="rlr-tooltip">
      <p className="rlr-tooltip-label">{label}</p>
      {payload.map((entry) => {
        const dataPoint = entry.payload;
        const lineConfig = entry.dataKey
          ? entry.payload.__config?.lines?.find((l) => l.key === entry.dataKey)
          : null;
        let pointColor = entry.color;

        if (lineConfig?.colorKey && dataPoint[lineConfig.colorKey]) {
          pointColor =
            STATUS_COLORS[dataPoint[lineConfig.colorKey]] || entry.color;
        }

        return (
          <p
            key={entry.dataKey}
            style={{ color: pointColor }}
            className="rlr-tooltip-row"
          >
            <span
              className="rlr-tooltip-dot"
              style={{ backgroundColor: pointColor }}
            />
            {entry.name}:{" "}
            <strong>
              {entry.value !== null && entry.value !== undefined
                ? Number(entry.value).toFixed(2)
                : "—"}
            </strong>
          </p>
        );
      })}
    </div>
  );
};

// ─── Custom dot component ───────────────────────────────────────────────────
const CustomDot = (props) => {
  const { cx, cy, stroke, payload, dataKey } = props;
  if (cy === null || cy === undefined || isNaN(cy)) return null;

  // Find which line this dot belongs to
  const lineConfig = payload.__config?.lines?.find((l) => l.key === dataKey);
  const statusColor = lineConfig?.colorKey
    ? STATUS_COLORS[payload[lineConfig.colorKey]]
    : lineConfig?.color || stroke;

  const finalColor = statusColor || stroke;

  return (
    <circle
      cx={cx}
      cy={cy}
      r={3}
      fill={finalColor}
      stroke={finalColor}
      strokeWidth={1}
    />
  );
};

const CustomActiveDot = (props) => {
  const { cx, cy, stroke, payload, dataKey } = props;
  if (cy === null || cy === undefined || isNaN(cy)) return null;

  const lineConfig = payload.__config?.lines?.find((l) => l.key === dataKey);
  const statusColor = lineConfig?.colorKey
    ? STATUS_COLORS[payload[lineConfig.colorKey]]
    : lineConfig?.color || stroke;
  const finalColor = statusColor || stroke;

  // console.log("finalColor : ", finalColor);

  return (
    <circle
      cx={cx}
      cy={cy}
      r={5}
      fill={finalColor}
      stroke={finalColor}
      strokeWidth={1}
    />
  );
};

const ChartCard = ({ config, data }) => {
  const hasData = data && data.length > 0;

  const yAxis = useMemo(() => {
    if (!hasData) return { domain: ["auto", "auto"], ticks: undefined };
    const values = [];
    data.forEach((row) => {
      config.lines.forEach((line) => {
        const v = Number(row[line.key]);
        if (!isNaN(v)) values.push(v);
      });
    });
    if (values.length === 0)
      return { domain: ["auto", "auto"], ticks: undefined };
    const min = Math.min(...values);
    const max = Math.max(...values);
    const range = max - min || (max !== 0 ? Math.abs(max) * 0.1 : 1);

    // Add 15% padding to keep data away from the edges
    const padding = range * 0.15;
    const rawLower = Math.max(0, min - padding);
    const rawUpper = max + padding;

    const visibleRange = rawUpper - rawLower || 1;
    const roughStep = visibleRange / 6;
    const magnitude = Math.pow(10, Math.floor(Math.log10(roughStep || 1)));
    const n = roughStep / magnitude;
    let step = magnitude;
    if (n > 7.5) step = 10 * magnitude;
    else if (n > 3) step = 5 * magnitude;
    else if (n > 1.5) step = 2 * magnitude;

    step = Math.max(1, Math.round(step));
    const lower = Math.max(0, Math.floor(rawLower / step) * step);
    const upper = Math.ceil(rawUpper / step) * step;

    const ticks = [];
    for (let t = lower; t <= upper; t += step) ticks.push(t);
    return { domain: [lower, upper], ticks };
  }, [data, config]);

  // Only render lines that actually have data points
  const visibleLines = useMemo(() => {
    return config.lines.filter((line) =>
      data.some(
        (row) =>
          row[line.key] !== null &&
          row[line.key] !== undefined &&
          !isNaN(Number(row[line.key])),
      ),
    );
  }, [data, config]);

  // Inject config into data points so the CustomTooltip/CustomDot components can access it
  const enrichedData = useMemo(() => {
    if (!data) return [];
    return data.map((item) => ({ ...item, __config: config }));
  }, [data, config]);

  // Generate unique IDs for gradients to avoid collisions
  const chartId = useMemo(
    () => `chart-${config.title.replace(/[^a-zA-Z0-9]/g, "-")}`,
    [config.title],
  );

  return (
    <div className="rlr-chart-card">
      <h3 className="rlr-chart-title">{config.title}</h3>
      {!hasData ? (
        <div className="rlr-no-data">No data available</div>
      ) : (
        <ResponsiveContainer width="100%" height={250}>
          <LineChart
            data={enrichedData}
            margin={{ top: 10, right: 24, left: 0, bottom: 4 }}
          >
            <defs>
              {visibleLines.map((line) => {
                const lIdx = config.lines.findIndex((l) => l.key === line.key);
                const gradId = `${chartId}-grad-${lIdx}`;
                if (!line.colorKey) return null;

                return (
                  <linearGradient
                    key={gradId}
                    id={gradId}
                    x1="0%"
                    y1="0%"
                    x2="100%"
                    y2="0%"
                  >
                    {enrichedData.map((point, pIdx) => {
                      const stopOffset =
                        enrichedData.length > 1
                          ? (pIdx / (enrichedData.length - 1)) * 100
                          : 0;
                      const statusColor =
                        STATUS_COLORS[point[line.colorKey]] || line.color;

                      return (
                        <stop
                          key={pIdx}
                          offset={`${stopOffset}%`}
                          stopColor={statusColor}
                        />
                      );
                    })}
                  </linearGradient>
                );
              })}
            </defs>
            <CartesianGrid strokeDasharray="3 3" stroke="#E8EDF2" />
            <XAxis
              dataKey="growth_phase"
              tick={{ fontSize: 11, fill: "#555" }}
              interval={0}
              angle={-18}
              textAnchor="end"
              height={50}
            />
            <YAxis
              tick={{ fontSize: 11, fill: "#555" }}
              width={52}
              domain={yAxis.domain}
              ticks={yAxis.ticks}
              allowDecimals={false}
            />
            <Tooltip content={<CustomTooltip />} />
            <Legend
              wrapperStyle={{ fontSize: "11px", paddingTop: "4px" }}
              iconType="circle"
            />
            {visibleLines.map((line) => {
              const lIdx = config.lines.findIndex((l) => l.key === line.key);
              const gradId = `${chartId}-grad-${lIdx}`;
              const strokeColor = line.colorKey
                ? `url(#${gradId})`
                : line.color;

              return (
                <Line
                  key={line.key}
                  type="monotone"
                  dataKey={line.key}
                  name={line.label}
                  stroke={strokeColor}
                  strokeWidth={2}
                  dot={<CustomDot />}
                  activeDot={<CustomActiveDot />}
                  connectNulls
                />
              );
            })}
          </LineChart>
        </ResponsiveContainer>
      )}
    </div>
  );
};

const AverageRoomLogReport = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { setShowLoginPopup } = useAuth();
  const httpService = new HttpService(setShowLoginPopup);
  const [rooms, setRooms] = useState([]);
  const [roomTab, setRoomTab] = useState(0);
  const [loading, setLoading] = useState(false);
  const [chartData, setChartData] = useState([]);
  const [cultivars, setCultivars] = useState({});
  const [activeEpicenter, setActiveEpicenter] = useState("01");
  const [plantedDate, setPlantedDate] = useState(null);
  const [harvestedDate, setHarvestedDate] = useState(null);

  const [selectedCategories, setSelectedCategories] = useState([
    "Water Temperature",
    "PH",
    "TDS",
  ]);
  const [selectedCharts, setSelectedCharts] = useState(() => {
    const defaults = [];
    CATEGORY_CONFIG.forEach((cat) => {
      cat.charts.forEach((chart) => {
        if (chart.includes("Epi 01")) {
          defaults.push(chart);
        }
      });
    });
    return defaults;
  });

  const toggleCategory = (catName) => {
    setSelectedCategories((prev) => {
      const isSelected = prev.includes(catName);
      if (isSelected) {
        const cat = CATEGORY_CONFIG.find((c) => c.name === catName);
        setSelectedCharts((charts) =>
          charts.filter((c) => !cat.charts.includes(c)),
        );
        return prev.filter((c) => c !== catName);
      } else {
        const cat = CATEGORY_CONFIG.find((c) => c.name === catName);
        setSelectedCharts((charts) => [
          ...new Set([...charts, ...cat.defaultSelected]),
        ]);
        return [...prev, catName];
      }
    });
  };

  const toggleChart = (chartTitle, catName) => {
    setSelectedCharts((prev) => {
      const isSelected = prev.includes(chartTitle);
      let next = isSelected
        ? prev.filter((t) => t !== chartTitle)
        : [...prev, chartTitle];
      const cat = CATEGORY_CONFIG.find((c) => c.name === catName);
      const anyRemaining = cat.charts.some((t) => next.includes(t));
      if (!anyRemaining) {
        setSelectedCategories((p) => p.filter((c) => c !== catName));
      } else if (!selectedCategories.includes(catName)) {
        setSelectedCategories((p) => [...p, catName]);
      }
      return next;
    });
  };

  const handleReset = () => {
    setSelectedCategories([]);
    setSelectedCharts([]);
    setActiveEpicenter(null);
  };

  const toggleEpicenter = useCallback((epiNum) => {
    const epiStr = `Epi ${epiNum.padStart(2, "0")}`;
    const allEpiCharts = [];
    CATEGORY_CONFIG.forEach((cat) => {
      cat.charts.forEach((chart) => {
        if (chart.includes(epiStr)) {
          allEpiCharts.push(chart);
        }
      });
    });

    // Replace current selection with only this epicenter's charts
    setSelectedCharts(allEpiCharts);
    setActiveEpicenter(epiNum);

    // Update categories: only those that have charts in allEpiCharts
    const nextCategories = [];
    CATEGORY_CONFIG.forEach((cat) => {
      if (cat.charts.some((t) => allEpiCharts.includes(t))) {
        nextCategories.push(cat.name);
      }
    });
    setSelectedCategories(nextCategories);
  }, []);

  const fetchRooms = useCallback(async () => {
    try {
      const res = await httpService.get("rooms?sortBy=name&sortOrder=ASC");
      const roomsData = res?.data?.data || [];
      const filtered = roomsData
        .filter((room) => {
          if (!room.name) return false;
          const name = room.name.trim().toLowerCase();
          return (
            /^room\s([1-9][0-9]?)$/.test(name) ||
            ["clone room", "mom room"].includes(name)
          );
        })
        .sort((a, b) => {
          const getNumber = (name) => {
            const match = name.match(/\d+/);
            return match ? Number(match[0]) : Infinity;
          };
          return getNumber(a.name) - getNumber(b.name);
        });
      setRooms(filtered);
    } catch {
      setRooms([]);
    }
  }, []);

  const fetchHistoryData = useCallback(async (roomId) => {
    if (!roomId) return;
    setLoading(true);
    try {
      const res = await httpService.get(
        `room-logs/historical-harvest/${roomId}`,
      );
      const history = res?.data?.history || {};
      const cultivarsData = res?.data?.cultivars || {};
      setCultivars(cultivarsData);
      if (res?.data?.plantedDate) {
        setPlantedDate(res?.data?.plantedDate);
      }
      if (res?.data?.harvestedDate) {
        setHarvestedDate(res?.data?.harvestedDate);
      }
      const phasesMap = {};
      Object.keys(history).forEach((epi) => {
        history[epi].forEach((row) => {
          const phase = row.growth_phase_name;
          if (!phasesMap[phase]) {
            phasesMap[phase] = {
              growth_phase: phase,
              growth_phase_id: row.growth_phase_id,
            };
          }
          phasesMap[phase][`${epi}_latest_water_temp`] = row.latest_water_temp;
          phasesMap[phase][`${epi}_avg_water_temp`] = row.avg_water_temp;
          phasesMap[phase][`${epi}_latest_ph`] = row.latest_ph;
          phasesMap[phase][`${epi}_avg_ph`] = row.avg_ph;
          phasesMap[phase][`${epi}_latest_tds`] = row.latest_tds;
          phasesMap[phase][`${epi}_avg_tds`] = row.avg_tds;
          phasesMap[phase][`${epi}_ph_target`] = row.ph_target;
          phasesMap[phase][`${epi}_tds_target`] = row.tds_target;
          phasesMap[phase][`${epi}_water_temp_target`] = row.water_temp_target;
          phasesMap[phase][`${epi}_latest_water_temp_color`] =
            row.latest_water_temp_color;
          phasesMap[phase][`${epi}_latest_ph_color`] = row.latest_ph_color;
          phasesMap[phase][`${epi}_latest_tds_color`] = row.latest_tds_color;
        });
      });

      const sortedData = Object.values(phasesMap).sort(
        (a, b) => a.growth_phase_id - b.growth_phase_id,
      );
      setChartData(sortedData);
    } catch {
      setChartData([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchRooms();
  }, [fetchRooms]);

  useEffect(() => {
    if (rooms.length === 0) return;

    // Check if roomId is passed in URL
    const urlRoomId = location.state?.roomId;

    let targetIdx = -1;
    if (urlRoomId) {
      targetIdx = rooms.findIndex((r) => String(r.id) === String(urlRoomId));
    }

    if (targetIdx === -1) {
      const idx = rooms.findIndex((r) => r.has_archive === true);
      targetIdx = idx !== -1 ? idx : 0;
    }

    setRoomTab(targetIdx);
    if (rooms[targetIdx]) {
      fetchHistoryData(rooms[targetIdx].id);
    }
  }, [rooms, fetchHistoryData, location.search]);

  const handleRoomChange = useCallback(
    (_, newIdx) => {
      setRoomTab(newIdx);
      if (rooms[newIdx]) {
        fetchHistoryData(rooms[newIdx].id);
        toggleEpicenter("01");
      }
    },
    [rooms, fetchHistoryData, toggleEpicenter],
  );

  return (
    <div className="company">
      {loading && <FullScreenLoader />}
      <div className="page-title">
        <div className="title-wrap">
          <h1>
            <span className="back">
              <BsArrowLeft
                onClick={() =>
                  navigate(
                    `${location?.state?.from ? "/" + location?.state?.from : "/roomlogreport"}`,
                    {
                      state: {
                        roomId: location?.state?.roomId,
                        harvested_date: location?.state?.harvested_date,
                        currentContentTab: 2,
                      },
                    },
                  )
                }
                style={{ strokeWidth: "1.5px" }}
              />
            </span>
            Cultivar Comparison
          </h1>
        </div>
      </div>
      <div className="common_content">
        <Tabs
          value={roomTab}
          onChange={handleRoomChange}
          variant="scrollable"
          scrollButtons={false}
          sx={{ ...tabsSx, mb: 3 }}
        >
          {rooms.map((room) => (
            <Tab
              key={room.id}
              label={room.name}
              disabled={!room.has_archive}
              sx={{
                "&.Mui-disabled": {
                  color: "#A1A2A1",
                  opacity: 0.5,
                },
              }}
            />
          ))}
        </Tabs>

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: 16,
            marginBottom: 12,
            justifyContent: "space-between",
          }}
        >
          <div className="form_field_wrapper d-flex align-items-center m-0">
            {Object.entries(cultivars).map(([epi, cultivar]) => {
              const epiNum = epi.replace("E", "").padStart(2, "0");
              const epiStr = `Epi ${epiNum}`;
              const allEpiCharts = [];
              CATEGORY_CONFIG.forEach((cat) => {
                cat.charts.forEach((chart) => {
                  if (chart.includes(epiStr)) allEpiCharts.push(chart);
                });
              });
              const isFullySelected = allEpiCharts.every((c) =>
                selectedCharts.includes(c),
              );

              return (
                <React.Fragment key={epi}>
                  <label
                    className="ms-2 d-flex align-items-center"
                    style={{ cursor: "pointer" }}
                    onClick={() => toggleEpicenter(epiNum)}
                  >
                    <span
                      style={{
                        color:
                          activeEpicenter === epiNum ? "#681da8" : "#007bff",
                        textDecoration: "underline",
                        fontWeight: 600,
                      }}
                    >
                      {`Epi ${epi.replace("E", "")}`}
                    </span>
                    <span className="ms-1" style={{ fontWeight: 600 }}>
                      :
                    </span>
                    <span
                      className="ms-2 me-4"
                      style={{
                        color: activeEpicenter === epiNum ? "#681da8" : "",
                        textDecoration: "underline",
                        fontWeight: 400,
                      }}
                    >
                      {cultivar}
                    </span>
                  </label>
                </React.Fragment>
              );
            })}
          </div>
          <div className="d-flex justify-content-between">
            <div className="d-flex align-items-center me-2">
              <span className="me-1" style={{ fontWeight: 600 }}>
                Planted Date :
              </span>
              <span>
                {plantedDate && dayjs(plantedDate).format("MM-DD-YYYY")}
              </span>
            </div>
            <div className="d-flex align-items-center">
              <span className="me-1" style={{ fontWeight: 600 }}>
                Harvested Date :
              </span>
              <span>
                {harvestedDate && dayjs(harvestedDate).format("MM-DD-YYYY")}
              </span>
            </div>
          </div>
        </div>

        <div className="d-flex flex-nowrap mb-0" style={{ gap: "10px" }}>
          <div className="rlr-filter-grid flex-grow-1">
            <div className="d-flex flex-wrap gap-2 w-100 mb-1">
              {CATEGORY_CONFIG.map((cat) => {
                const isCatActive = selectedCategories.includes(cat.name);
                return (
                  <div key={cat.name} className="rlr-filter-card">
                    <div className="rlr-filter-header">
                      <label className="rlr-checkbox-wrapper">
                        <input
                          type="checkbox"
                          checked={isCatActive}
                          onChange={() => toggleCategory(cat.name)}
                        />
                        <span className="rlr-checkbox-label">{cat.name}</span>
                      </label>
                    </div>
                    <div className="rlr-filter-items">
                      {cat.charts.map((chartTitle) => {
                        const isChartActive =
                          selectedCharts.includes(chartTitle);
                        const isOtherEpi =
                          activeEpicenter &&
                          !chartTitle.includes(`Epi ${activeEpicenter}`);

                        let displayLabel = chartTitle
                          .replace(" (°F)", "")
                          .replace(" (ppm)", "")
                          .replace(" (PH)", "");
                        if (displayLabel.includes("Water Temp – ")) {
                          displayLabel =
                            displayLabel.replace("Water Temp – ", "") + " Temp";
                        }
                        return (
                          <button
                            key={chartTitle}
                            type="button"
                            className={`rlr-filter-tag ${isChartActive ? "active" : ""}`}
                            onClick={() => toggleChart(chartTitle, cat.name)}
                            disabled={isOtherEpi}
                            style={
                              isOtherEpi
                                ? { opacity: 0.5, cursor: "not-allowed" }
                                : {}
                            }
                          >
                            {displayLabel}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
          <div className="rlr-filter-reset-wrapper">
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleReset}
            >
              Reset
            </button>
          </div>
        </div>

        {rooms.length === 0 ? (
          <div className="rlr-empty-state">No rooms found.</div>
        ) : (
          <div className="rlr-charts-grid">
            {CHART_CONFIGS.filter((cfg) =>
              selectedCharts.includes(cfg.title),
            ).map((cfg) => (
              <ChartCard key={cfg.title} config={cfg} data={chartData} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AverageRoomLogReport;
